import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Download, Fingerprint } from "lucide-react";
import { api, fmtFecha } from "@/lib/api";
import VisorArchivo from "@/components/VisorArchivo";
import { hoyISOBA } from "@/components/ClockBA";
import { Badge, Card, Modal, PageHeader, btnSec, inputCls } from "@/components/rrhh/ui";

const TIPOS = ["entrada", "salida", "desayuno", "almuerzo", "merienda"];
const ESTADO_COLOR = {
  completa: "verde", incompleta: "amarillo", ausente: "rojo", injustificada: "rojo",
  justificada: "azul", vacaciones: "azul", licencia: "azul", feriado: "azul", franco_trabajado: "azul",
};

export default function FichadasPage() {
  const [params] = useSearchParams();
  const [desde, setDesde] = useState(hoyISOBA().slice(0, 8) + "01");
  const [hasta, setHasta] = useState(hoyISOBA());
  const [localId, setLocalId] = useState("");
  const [empleadoId, setEmpleadoId] = useState(params.get("empleado") || "");
  const [tipo, setTipo] = useState("");
  const [fotoVer, setFotoVer] = useState(null);
  const [jornadaVer, setJornadaVer] = useState(null); // { empleado_id, fecha }

  const { data: locales } = useQuery({ queryKey: ["locales"], queryFn: async () => (await api.get("/locales")).data });
  const { data: empleados } = useQuery({ queryKey: ["empleados"], queryFn: async () => (await api.get("/empleados")).data });
  const filtros = useMemo(() => ({
    fecha_desde: desde || undefined, fecha_hasta: hasta || undefined,
    local_id: localId || undefined, empleado_id: empleadoId || undefined, tipo: tipo || undefined,
  }), [desde, hasta, localId, empleadoId, tipo]);

  const { data: fichadas, isLoading } = useQuery({
    queryKey: ["fichadas", filtros],
    queryFn: async () => (await api.get("/rrhh/fichadas", { params: filtros })).data,
    refetchInterval: 5000,
  });

  const { data: jornadaData } = useQuery({
    queryKey: ["jornada", jornadaVer],
    queryFn: async () => (await api.get("/rrhh/jornada", { params: jornadaVer })).data,
    enabled: !!jornadaVer,
  });

  const qs = new URLSearchParams(Object.entries(filtros).filter(([, v]) => v)).toString();

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="pagina-fichadas">
      <PageHeader
        titulo="Fichadas"
        subtitulo="Todo lo que se fichó, con su foto y su local. Solo lectura: los ajustes se hacen desde la ficha de cada persona."
        testid="header-fichadas"
      >
        <a
          href={`${process.env.REACT_APP_BACKEND_URL}/api/rrhh/fichadas.csv?${qs}`}
          className={`${btnSec} flex items-center gap-2`}
          data-testid="exportar-csv"
        >
          <Download size={14} /> Exportar CSV
        </a>
      </PageHeader>

      <Card testid="card-filtros-fichadas" className="mb-5" bodyCls="p-4">
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className={inputCls} data-testid="filtro-desde" />
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className={inputCls} data-testid="filtro-hasta" />
          <select value={localId} onChange={(e) => setLocalId(e.target.value)} className={inputCls} data-testid="filtro-local">
            <option value="">Todos los locales</option>
            {locales?.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
          </select>
          <select value={empleadoId} onChange={(e) => setEmpleadoId(e.target.value)} className={inputCls} data-testid="filtro-empleado">
            <option value="">Todos los empleados</option>
            {empleados?.map((e) => <option key={e.id} value={e.id}>{e.apellido}, {e.nombre} ({e.legajo})</option>)}
          </select>
          <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={inputCls} data-testid="filtro-tipo-fichada">
            <option value="">Todos los tipos</option>
            {TIPOS.map((t) => <option key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</option>)}
          </select>
          <span className="ml-auto text-xs text-gray-500">{fichadas?.length ?? "…"} fichadas</span>
        </div>
      </Card>

      <Card testid="card-fichadas" bodyCls="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/8 text-left text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-5 py-3 font-semibold">Fecha y hora</th>
                <th className="px-4 py-3 font-semibold">Empleado</th>
                <th className="px-4 py-3 font-semibold">Legajo</th>
                <th className="px-4 py-3 font-semibold">Local</th>
                <th className="px-4 py-3 font-semibold">Tipo</th>
                <th className="px-4 py-3 font-semibold">Origen</th>
                <th className="px-5 py-3 font-semibold">Foto</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-500">Cargando…</td></tr>}
              {fichadas?.map((f) => (
                <tr
                  key={f.id}
                  className={`cursor-pointer border-b border-white/5 transition-colors last:border-0 hover:bg-[#2DD4BF]/[0.04] ${f.anulada ? "opacity-45" : ""}`}
                  onClick={() => setJornadaVer({ empleado_id: f.empleado_id, fecha: f.fecha_hora.slice(0, 10) })}
                  data-testid={`fichada-${f.id.slice(0, 8)}`}
                >
                  <td className="whitespace-nowrap px-5 py-3 font-mono-num text-xs">
                    <span className="text-gray-400">{fmtFecha(f.fecha_hora)}</span>{" "}
                    <span className={`font-semibold ${f.anulada ? "text-gray-500 line-through" : "text-white"}`}>{f.fecha_hora.slice(11, 16)}</span>
                  </td>
                  <td className="px-4 py-3 text-white">{f.empleado_nombre}</td>
                  <td className="px-4 py-3 font-mono-num text-gray-400">{f.legajo}</td>
                  <td className="px-4 py-3 text-gray-300">{f.local_nombre}</td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1.5">
                      <Badge color={f.tipo === "entrada" ? "verde" : f.tipo === "salida" ? "rojo" : "gris"}>{f.tipo}</Badge>
                      {f.anulada && <Badge color="rojo">Anulada</Badge>}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">
                    {f.origen === "rrhh_manual" ? "manual de RRHH" : f.origen === "rrhh" ? "corregida por RRHH" : f.origen}
                  </td>
                  <td className="px-5 py-3">
                    {f.foto_url ? (
                      <button onClick={(e) => { e.stopPropagation(); setFotoVer(f.foto_url); }} data-testid="foto-ampliar">
                        <img src={`${process.env.REACT_APP_BACKEND_URL}${f.foto_url}`} alt="Foto" className="h-8 w-8 rounded-lg border border-white/10 object-cover transition-transform hover:scale-110" />
                      </button>
                    ) : <span className="text-gray-600">—</span>}
                  </td>
                </tr>
              ))}
              {fichadas?.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-12 text-center">
                    <Fingerprint size={26} className="mx-auto text-gray-600" />
                    <p className="mt-3 text-sm font-semibold text-gray-300">No hay fichadas con estos filtros</p>
                    <p className="text-xs text-gray-500">Probá con otro rango de fechas u otro local.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {jornadaVer && jornadaData && (
        <Modal
          titulo={`Jornada del ${fmtFecha(jornadaVer.fecha)} — ${jornadaData.empleado} (${jornadaData.legajo})`}
          onClose={() => setJornadaVer(null)}
          testid="modal-jornada"
        >
          {jornadaData.jornada && (
            <div className="mb-4 flex flex-wrap items-center gap-2 text-xs">
              <Badge color={ESTADO_COLOR[jornadaData.jornada.estado] || "gris"} testid="jornada-estado">{jornadaData.jornada.estado.replaceAll("_", " ")}</Badge>
              {jornadaData.jornada.tardanza_min > 0 && <Badge color="amarillo">Tardanza {jornadaData.jornada.tardanza_min} min</Badge>}
              {jornadaData.jornada.salida_anticipada_min > 0 && <Badge color="amarillo">Salida anticipada {jornadaData.jornada.salida_anticipada_min} min</Badge>}
              <Badge color={jornadaData.jornada.hora_adicional ? "verde" : "gris"}>
                {jornadaData.jornada.hora_adicional ? "Con adicional" : "Sin adicional"}
              </Badge>
              <span className="text-gray-400">Pausas: {jornadaData.jornada.minutos_pausa} min</span>
            </div>
          )}
          {jornadaData.jornada?.observaciones && (
            <p className="mb-4 rounded-xl bg-white/5 px-3 py-2 text-xs text-gray-400">{jornadaData.jornada.observaciones}</p>
          )}
          <div className="space-y-2">
            {jornadaData.fichadas.length === 0 && <p className="text-sm text-gray-500">Sin fichadas ese día.</p>}
            {jornadaData.fichadas.map((f) => (
              <div key={f.id} className={`flex items-center gap-3 rounded-xl border border-white/5 bg-[#0B0F14] px-3.5 py-2.5 ${f.anulada ? "opacity-45" : ""}`}>
                <span className={`font-mono-num text-sm font-semibold text-white ${f.anulada ? "line-through" : ""}`}>{f.fecha_hora.slice(11, 16)}</span>
                <Badge color={f.tipo === "entrada" ? "verde" : f.tipo === "salida" ? "rojo" : "gris"}>{f.tipo}</Badge>
                {f.anulada && <Badge color="rojo">Anulada</Badge>}
                <span className="text-xs text-gray-500">{f.local_nombre}</span>
                {f.foto_url && (
                  <button className="ml-auto" onClick={() => setFotoVer(f.foto_url)}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${f.foto_url}`} alt="Foto" className="h-8 w-8 rounded-lg border border-white/10 object-cover" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </Modal>
      )}

      {fotoVer && <VisorArchivo url={fotoVer} onClose={() => setFotoVer(null)} titulo="Foto de la fichada" />}
    </motion.div>
  );
}
